/* eslint-disable */
/**
 * Gatsby Browser APIs
 * Handles third-party scripts, form tracking, and analytics
 */

const axios = require('axios')
const {
  initPerformanceOptimizations,
  loadGTMOptimized,
  checkCountryOptimized,
} = require('./src/utils/thirdPartyOptimizer')

// Constants
const TARGET_FORM_IDS = [
  '517ebc0a-e3b2-48e7-994d-19507b711e00',
  'c169c509-7422-4098-bdd2-085a67d82092',
  'e23ab1ed-02c7-42c1-90bc-ecd78ae786c3',
  '3ba0360d-0fb0-4473-84c9-a7362657431c',
  '568567fd-2abb-43a4-b030-a6e2d7875387',
]
const GTM_ID = 'TDN6QG5'
const REVENUE_HERO_ROUTER_ID = '1226'
const STORAGE_KEY = 'cookieConsentConfig'
const SESSION_KEY = 'allowGtm'

/**
 * Cookie Consent Manager
 * Handles GDPR cookie consent and GTM loading
 */
const createCookieConsentManager = () => {
  const getStoredConfig = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY))
    } catch {
      return null
    }
  }

  const setInitialState = (country) => {
    const config = {
      showConsentBar: true,
      allowCookie: false,
      country: country,
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config))
    sessionStorage.setItem(SESSION_KEY, 'false')
    console.log('Initialized cookie consent:', config)
  }

  const loadGTMIfAllowed = () => {
    const allowed = sessionStorage.getItem(SESSION_KEY)
    if (allowed === 'true') {
      loadGTMOptimized(GTM_ID)
    }
  }

  const triggerStorageEvent = () => {
    const evt = document.createEvent('StorageEvent')
    evt.initStorageEvent('storage', false, false, SESSION_KEY, 'oldValue', 'newValue', null, window.localStorage)
    window.dispatchEvent(evt)
  }

  const setupStorageListener = () => {
    triggerStorageEvent()

    window.addEventListener(
      'storage',
      () => {
        loadGTMIfAllowed()
      },
      false
    )
  }

  const init = async () => {
    initPerformanceOptimizations()

    const country = await checkCountryOptimized()
    const storedState = getStoredConfig()

    if (!storedState) {
      setInitialState(country)
    }

    loadGTMIfAllowed()
    setupStorageListener()
  }

  return { init }
}

/**
 * Revenue Hero Integration
 * Handles form submission routing through Revenue Hero
 */
const createRevenueHeroManager = () => {
  const handleFormSubmit = (event) => {
    const { data } = event

    if (data.type !== 'hsFormCallback' || data.eventName !== 'onFormSubmitted') {
      return
    }

    const formId = data.id

    if (!TARGET_FORM_IDS.includes(formId)) {
      return
    }

    const formValues = data.data.submissionValues
    const RevenueHero = window.RevenueHero

    if (!RevenueHero) {
      console.error('RevenueHero not loaded')
      return
    }

    const hero = new RevenueHero({ routerId: REVENUE_HERO_ROUTER_ID })
    hero.submit(formValues).then((sessionData) => {
      hero.dialog.open(sessionData)
    })
  }

  const init = () => {
    if (typeof window === 'undefined') return

    if (window.addEventListener) {
      window.addEventListener('message', handleFormSubmit, false)
    } else if (window.attachEvent) {
      window.attachEvent('onmessage', handleFormSubmit)
    }
  }

  return { init }
}

/**
 * Enhanced Conversion Tracking
 * Pushes form data to GTM dataLayer for offline conversion tracking
 */
const createConversionTracker = () => {
  const handleFormSubmit = (event) => {
    const { data } = event

    if (data?.type !== 'hsFormCallback' || data.eventName !== 'onFormSubmitted') {
      return
    }

    const submissionValues = data.data?.submissionValues || {}
    const { email, phone } = submissionValues

    if (email || phone) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'enhanced_conversion',
        email: email,
        phone_number: phone,
      })
    }
  }

  const init = () => {
    if (typeof window === 'undefined') return

    window.addEventListener('message', handleFormSubmit, false)
  }

  return { init }
}

/**
 * Third-Party Scripts Loader
 * Lazy loads third-party scripts on user interaction
 */
const createThirdPartyLoader = () => {
  const events = ['scroll', 'mousedown', 'touchstart', 'keydown']
  let loaded = false
  let eventHandlers = []

  const load = () => {
    if (loaded) return
    loaded = true

    // Remove all event listeners
    eventHandlers.forEach(({ event, handler }) => {
      document.removeEventListener(event, handler, { passive: true })
    })
    eventHandlers = []

    // Initialize cookie consent manager
    const cookieConsent = createCookieConsentManager()
    cookieConsent.init()
  }

  const init = () => {
    events.forEach((event) => {
      const handler = load
      document.addEventListener(event, handler, { passive: true })
      eventHandlers.push({ event, handler })
    })
  }

  return { init }
}

/**
 * Gatsby Browser API: onRouteUpdate
 * Called when route changes
 */
exports.onRouteUpdate = () => {
  // Custom scripts are handled elsewhere, no action needed here
}

/**
 * Gatsby Browser API: onClientEntry
 * Called when Gatsby app first loads in browser
 */
exports.onClientEntry = () => {
  const thirdPartyLoader = createThirdPartyLoader()
  const revenueHero = createRevenueHeroManager()
  const conversionTracker = createConversionTracker()

  thirdPartyLoader.init()
  revenueHero.init()
  conversionTracker.init()
}
